import { useState } from "react";
import { useAuth, homeFor } from "@/lib/auth";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const DEMO_ACCOUNTS = [
  { label: "Company Admin — Cape Grand", email: "admin@capegrand.example", password: "Admin@123" },
  { label: "Supervisor — Cape Grand", email: "david@capegrand.example", password: "Super@123" },
  { label: "Employee — Cape Grand", email: "sara@capegrand.example", password: "Work@123" },
  { label: "Company Admin — Savannah", email: "admin@savannah.example", password: "Admin@123" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      nav(homeFor(res.user));
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  const quickLogin = (acc) => {
    setEmail(acc.email);
    setPassword(acc.password);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="relative hidden md:block overflow-hidden" style={{ background: "hsl(222 44% 14%)" }}>
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1723516908282-b3c795e9416a"
            alt="Facility"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="absolute top-1/3 -right-32 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <span className="font-display font-black text-primary-foreground">S</span>
            </div>
            <span className="font-display font-bold tracking-tight text-lg">Sparkle</span>
          </div>
          <div className="space-y-6">
            <div className="text-[0.7rem] tracking-widest uppercase text-primary font-semibold">Operations OS</div>
            <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] max-w-md">
              Operational excellence,<br/>
              <span className="text-white/60">audited to the minute.</span>
            </h1>
            <p className="text-sm max-w-md text-white/70 leading-relaxed">
              A multi-tenant workflow platform for hotels, restaurants, hospitals and
              commercial facilities. Every task, every check, every signature —
              tracked, timed and compliant.
            </p>
            <div className="grid grid-cols-3 gap-6 pt-4 border-t border-white/10 max-w-md">
              <div>
                <div className="font-display font-black text-2xl text-primary">3</div>
                <div className="text-[0.65rem] tracking-widest uppercase text-white/60 mt-1">tenants</div>
              </div>
              <div>
                <div className="font-display font-black text-2xl text-primary">4</div>
                <div className="text-[0.65rem] tracking-widest uppercase text-white/60 mt-1">roles</div>
              </div>
              <div>
                <div className="font-display font-black text-2xl text-primary">100%</div>
                <div className="text-[0.65rem] tracking-widest uppercase text-white/60 mt-1">audited</div>
              </div>
            </div>
          </div>
          <div className="text-[0.65rem] tracking-widest uppercase text-white/40">POC · v1.0</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30">
              <span className="font-display font-black">S</span>
            </div>
            <span className="font-display font-bold text-lg">Sparkle</span>
          </div>
          <div className="mb-8">
            <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground mb-2 font-semibold">Sign in</div>
            <h2 className="font-display text-3xl font-bold tracking-tight">
              Welcome back.
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Access your operations workspace.
            </p>
          </div>
          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 rounded-2xl text-base font-semibold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground font-semibold mb-3">Demo accounts</div>
            <div className="grid gap-2">
              {DEMO_ACCOUNTS.map((a) => (
                <button
                  key={a.email}
                  onClick={() => quickLogin(a)}
                  className="text-left px-4 py-2.5 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                  data-testid={`demo-${a.email}`}
                  type="button"
                >
                  <div className="text-sm font-medium">{a.label}</div>
                  <div className="text-xs text-muted-foreground font-mono">{a.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
